import React from "react"
import PropTypes from "prop-types"

// components
import {
    datePickerGenericFilter,
    enumOrderSort,
    enumSingleGenericFilter,
    textGenericFilter,
} from "shared/search/containers/Filter/Filters"
import { toggleGenericFilter, searchifySelectGenericFilter } from "shared/search/containers/Filter/FilterCreators"

import { allRegionsExceptUS, usRegions } from "shared/imports/regionConstants"

import {
    OfficialInOutboxFilterableListView,
    OfficialsAndStaffEUFilterableListView,
    SendEmailFilterableListView,
    StafferFilterableListView,
    SupporterFilterableListView,
    PressContactFilterableListView,
} from "shared/search/containers/FilterableListView/FilterableListViews"

import {
    ContactSelect,
    PressContactSelect,
    OfficialsAndStaffSelect,
    OfficialSelect,
    SendEmailSelect,
    StafferSelect,
} from "app/static/frontend/selects/containers/PieSelect"

const dateDefaultPropTypes = {
    displayLabel: PropTypes.string,
    filterComponent: PropTypes.func,
    filterKey: PropTypes.string,
    uniqueFilterId: PropTypes.string,

    noFuture: PropTypes.bool,
}

const defaultEnumPropTypes = {
    displayLabel: PropTypes.string,
    filterComponent: PropTypes.func,
    filterKey: PropTypes.string,
    uniqueFilterId: PropTypes.string,

    enumItems: PropTypes.array,
    helpText: PropTypes.string,
    sortBy: PropTypes.func,
}

const textPropsTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    placeholderText: PropTypes.string,
    displayLabel: PropTypes.string,
}

const createFilterComponent = (props) => {
    const FilterComponent = props.filterComponent
    // https://github.com/facebook/flow/issues/6644
    return <FilterComponent {...props} />
}
createFilterComponent.propTypes = {
    filterComponent: PropTypes.func,
}

// ReplyEmailReferencingFilterGroup
export const ReplyEmailFromAddressFilter = (props) => createFilterComponent(props)
ReplyEmailFromAddressFilter.propTypes = textPropsTypes
ReplyEmailFromAddressFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "replyEmailFromAddressFilter",
    filterKey: "from_address_search",
    placeholderText: "Search by From Address",
    displayLabel: "Search by From Address",
}

export const ReplyEmailForwardAddressFilter = (props) => createFilterComponent(props)
ReplyEmailForwardAddressFilter.propTypes = textPropsTypes
ReplyEmailForwardAddressFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "replyEmailForwardAddressFilter",
    filterKey: "forward_address_search",
    placeholderText: "Search by Forward Address",
    displayLabel: "Search by Forward Address",
}

export const ReplyEmailToAddressFilter = (props) => createFilterComponent(props)
ReplyEmailToAddressFilter.propTypes = textPropsTypes
ReplyEmailToAddressFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "replyEmailToAddressFilter",
    filterKey: "to_address_search",
    placeholderText: "Search by To Address",
    displayLabel: "Search by To Address",
}

export const ReplyEmailTextFilter = (props) => createFilterComponent(props)
ReplyEmailTextFilter.propTypes = textPropsTypes
ReplyEmailTextFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "replyEmailTextFilter",
    filterKey: "text_search",
    placeholderText: "Search by Email Text",
    displayLabel: "Search by Email Text",
}

export const ReplyEmailSubjectFilter = (props) => createFilterComponent(props)
ReplyEmailSubjectFilter.propTypes = textPropsTypes
ReplyEmailSubjectFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "replyEmailSubjectFilter",
    filterKey: "subject_search",
    placeholderText: "Search by Subject",
    displayLabel: "Search by Subject",
}

// ReplyEmailDateFilterGroup
export const ReplyEmailCreatedDateNoFutureFilter = (props) => createFilterComponent(props)
ReplyEmailCreatedDateNoFutureFilter.propTypes = defaultEnumPropTypes
ReplyEmailCreatedDateNoFutureFilter.defaultProps = {
    filterComponent: enumSingleGenericFilter,
    uniqueFilterId: "replyEmailCreatedDateNoFutureFilter",
    filterKey: "date_range",
    enumItems: DjangIO.app.events.models.DateRangeType.region_supported_items().filter(
        (item) =>
            ![
                DjangIO.app.events.models.DateRangeType.future.value,
                DjangIO.app.events.models.DateRangeType.next_month.value,
                DjangIO.app.events.models.DateRangeType.next_week.value,
            ].includes(item.value),
    ),
    sortBy: enumOrderSort,
    displayLabel: "Date Sent",
    helpText:
        "Filter for results that were added to quorum after a particular date " + "or within a specific date range.",
}

export const ReplyEmailCreatedDatePickerStartNoFutureFilter = (props) => createFilterComponent(props)
ReplyEmailCreatedDatePickerStartNoFutureFilter.propTypes = dateDefaultPropTypes
ReplyEmailCreatedDatePickerStartNoFutureFilter.defaultProps = {
    filterComponent: datePickerGenericFilter,
    uniqueFilterId: "replyEmailCreatedDatePickerStartNoFutureFilter",
    filterKey: "created__gt",
    displayLabel: "Start Date",
    noFuture: true,
    helpText: "Reply emails created after date selected. Excludes date selected.",
}

export const ReplyEmailCreatedDatePickerEndNoFutureFilter = (props) => createFilterComponent(props)
ReplyEmailCreatedDatePickerEndNoFutureFilter.propTypes = dateDefaultPropTypes
ReplyEmailCreatedDatePickerEndNoFutureFilter.defaultProps = {
    filterComponent: datePickerGenericFilter,
    uniqueFilterId: "replyEmailCreatedDatePickerEndNoFutureFilter",
    filterKey: "created__lt",
    displayLabel: "End Date",
    noFuture: true,
    helpText: "Reply emails created before date selected. Excludes date selected.",
}

// ReplyEmailSendEmailFilterGroup
export const ReplyEmailSendEmailFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={SendEmailFilterableListView} {...props} />
}
ReplyEmailSendEmailFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
}
ReplyEmailSendEmailFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "replyEmailSendEmailFilter",
    filterKey: "about_send_email",
    dataTypeLabel: "Outbox (Recipient) Emails",
    selectComponent: SendEmailSelect,
}

export const ReplyEmailOfficialsFilter = (props) => createFilterComponent(props)
ReplyEmailOfficialsFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    displayLabel: PropTypes.string,
    trueText: PropTypes.string,
    falseText: PropTypes.string,
    permissions: PropTypes.array,
}
ReplyEmailOfficialsFilter.defaultProps = {
    filterComponent: toggleGenericFilter,
    uniqueFilterId: "replyEmailOfficialsFilter",
    filterKey: "sent_to_officials",
    displayLabel: "Reply from Official",
    trueText: "Yes",
    falseText: "No",
    permissions: [
        DjangIO.app.models.PermissionType.legislators,
        DjangIO.app.models.PermissionType.executives,
        DjangIO.app.models.PermissionType.staffers,
    ],
}

export const ReplyEmailStaffersFilter = (props) => createFilterComponent(props)
ReplyEmailStaffersFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    displayLabel: PropTypes.string,
    trueText: PropTypes.string,
    falseText: PropTypes.string,
    regions: PropTypes.array,
    permissions: PropTypes.array,
}
ReplyEmailStaffersFilter.defaultProps = {
    filterComponent: toggleGenericFilter,
    uniqueFilterId: "replyEmailStaffersFilter",
    filterKey: "sent_to_staffers",
    displayLabel: "Reply from Staffer",
    regions: usRegions,
    trueText: "Yes",
    falseText: "No",
    permissions: [
        DjangIO.app.models.PermissionType.legislators,
        DjangIO.app.models.PermissionType.executives,
        DjangIO.app.models.PermissionType.staffers,
    ],
}

export const ReplyEmailPressContactsFilter = (props) => createFilterComponent(props)
ReplyEmailPressContactsFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    displayLabel: PropTypes.string,
    trueText: PropTypes.string,
    falseText: PropTypes.string,
    permissions: PropTypes.array,
}
ReplyEmailPressContactsFilter.defaultProps = {
    filterComponent: toggleGenericFilter,
    uniqueFilterId: "replyEmailPressContactsFilter",
    filterKey: "sent_to_press",
    displayLabel: "Reply from Press",
    trueText: "Yes",
    falseText: "No",
    permissions: [DjangIO.app.models.PermissionType.media_relations],
}

export const ReplyEmailContactsFilter = (props) => createFilterComponent(props)
ReplyEmailContactsFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    displayLabel: PropTypes.string,
    trueText: PropTypes.string,
    falseText: PropTypes.string,
    permissions: PropTypes.array,
}
ReplyEmailContactsFilter.defaultProps = {
    filterComponent: toggleGenericFilter,
    uniqueFilterId: "replyEmailContactsFilter",
    filterKey: "sent_to_supporters",
    displayLabel: "Reply from Contact",
    trueText: "Yes",
    falseText: "No",
    permissions: [DjangIO.app.models.PermissionType.custom_outbox],
}

// ReplyEmailSourceFilterGroup
export const ReplyEmailOfficialFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={OfficialInOutboxFilterableListView} {...props} />
}
ReplyEmailOfficialFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
    regions: PropTypes.array,
    permissions: PropTypes.array,
}
ReplyEmailOfficialFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "replyEmailOfficialFilter",
    filterKey: "about_official",
    dataTypeLabel: "Officials",
    selectComponent: OfficialSelect,
    regions: usRegions,
    permissions: [
        DjangIO.app.models.PermissionType.legislators,
        DjangIO.app.models.PermissionType.executives,
        DjangIO.app.models.PermissionType.staffers,
    ],
}

export const ReplyEmailStafferFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={StafferFilterableListView} {...props} />
}
ReplyEmailStafferFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
    regions: PropTypes.array,
    permissions: PropTypes.array,
}
ReplyEmailStafferFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "replyEmailStafferFilter",
    filterKey: "about_official",
    dataTypeLabel: DjangIO.app.models.QuorumDataType.staffer.plural,
    selectComponent: StafferSelect,
    regions: usRegions,
    permissions: [
        DjangIO.app.models.PermissionType.legislators,
        DjangIO.app.models.PermissionType.executives,
        DjangIO.app.models.PermissionType.staffers,
    ],
}

export const ReplyEmailOfficialsAndStaffersFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={OfficialsAndStaffEUFilterableListView} {...props} />
}
ReplyEmailOfficialsAndStaffersFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
    regions: PropTypes.array,
}
ReplyEmailOfficialsAndStaffersFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "replyEmailOfficialsAndStaffersFilter",
    filterKey: "about_official",
    dataTypeLabel: "Officials",
    selectComponent: OfficialsAndStaffSelect,
    regions: allRegionsExceptUS,
}

export const ReplyEmailPressContactFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={PressContactFilterableListView} {...props} />
}
ReplyEmailPressContactFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
    permissions: PropTypes.array,
}
ReplyEmailPressContactFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "replyEmailPressContactFilter",
    filterKey: "about_press",
    dataTypeLabel: DjangIO.app.models.QuorumDataType.press_contact.plural,
    selectComponent: PressContactSelect,
    permissions: [DjangIO.app.models.PermissionType.media_relations],
}

export const ReplyEmailContactFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={SupporterFilterableListView} {...props} />
}
ReplyEmailContactFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
    permissions: PropTypes.array,
}
ReplyEmailContactFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "replyEmailContactFilter",
    filterKey: "about_supporter",
    dataTypeLabel: "Contacts",
    selectComponent: ContactSelect,
    permissions: [DjangIO.app.models.PermissionType.custom_outbox],
}

// here
export const ReplyEmailTitleFilter = (props) => createFilterComponent(props)
ReplyEmailTitleFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    placeholderText: PropTypes.string,
    displayLabel: PropTypes.string,
}
ReplyEmailTitleFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "replyEmailTitleFilter",
    filterKey: "source_title_search",
    placeholderText: "Search by title",
    displayLabel: "Search by Job Title",
}

export const ReplyEmailContactOrganizationFilter = (props) => createFilterComponent(props)
ReplyEmailContactOrganizationFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    placeholderText: PropTypes.string,
    displayLabel: PropTypes.string,
}
ReplyEmailContactOrganizationFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "replyEmailContactOrganizationFilter",
    filterKey: "source_organization_search",
    placeholderText: "Search by organization name",
    displayLabel: "Search by Organization Name",
}

export const ReplyEmailEmailAddressFilter = (props) => createFilterComponent(props)
ReplyEmailEmailAddressFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    placeholderText: PropTypes.string,
    displayLabel: PropTypes.string,
    helpText: PropTypes.string,
}
ReplyEmailEmailAddressFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "replyEmailEmailAddressFilter",
    filterKey: "source_email_address_search",
    placeholderText: "Search by email address",
    displayLabel: "Search by Email Address",
    helpText: "You can search for text contained in multiple email addresses by separating your inputs with a comma",
}

export const ReplyEmailRecipientNameFilter = (props) => createFilterComponent(props)
ReplyEmailRecipientNameFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    placeholderText: PropTypes.string,
    displayLabel: PropTypes.string,
}
ReplyEmailRecipientNameFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "replyEmailRecipientNameFilter",
    filterKey: "source_recipient_name_search",
    placeholderText: "Search by name",
    displayLabel: "Search by Name",
}
